import { useState } from 'react'
import {
  CalendarPlus,
  RefreshCw,
  Trash2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Eye,
  EyeOff,
  Laptop,
  CloudUpload,
  ArrowUp,
  ArrowDown,
  Layers,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useCalendar } from '@/context/CalendarContext'
import { useApp } from '@/context/AppContext'
import {
  calendarPriority,
  type CalendarAccount,
  type CalendarPrivacy,
  type CalendarSource,
} from '@trackmind/core'
import { cn, formatDateTime } from '@/lib/utils'

/**
 * Manage connected calendars.
 *
 * Two-view: the same rows and the same controls at both widths, but the
 * per-calendar options are a 2-column grid on desktop and a single stack on
 * phones, and account actions collapse to icons below `md`. Every calendar
 * row is a one-line ~48px row with its options one tap away, per the mobile
 * density rule — someone with a dozen subscribed calendars still gets a
 * scannable list.
 */
export function CalendarSettings() {
  const {
    available,
    accounts,
    sources,
    loading,
    refreshing,
    lastRefreshedAt,
    error,
    connectAccount,
    disconnectAccount,
    setAccountSharing,
    updateSource,
    refresh,
  } = useCalendar()

  const [connecting, setConnecting] = useState(false)

  // The Google account the user signed in with for STORAGE, if that's how
  // they signed in. Offering its calendar with one tap is a shortcut, not a
  // gate: "Connect a calendar" below works identically for everyone,
  // including GitHub users, which is what CLAUDE.md §3.6 actually requires.
  const { authProvider, authState } = useApp()
  const storageEmail = authProvider === 'google' ? (authState?.user?.email ?? null) : null

  if (!available) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Calendar connections aren't configured for this deployment.
        </CardContent>
      </Card>
    )
  }

  const handleConnect = async (email?: string) => {
    setConnecting(true)
    try {
      await connectAccount(email ? { email } : {})
    } catch (e) {
      console.error('Failed to start calendar connect:', e)
      setConnecting(false)
    }
  }

  // Already connected as a calendar? Then there's nothing to offer.
  const storageAccountConnected =
    storageEmail !== null &&
    accounts.some((a) => a.email.toLowerCase() === storageEmail.toLowerCase())

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base md:text-lg flex flex-wrap items-center justify-between gap-2">
            <span>Connected calendars</span>
            <div className="flex items-center gap-2">
              {accounts.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refresh({ force: true })}
                  disabled={refreshing}
                >
                  <RefreshCw className={cn('h-4 w-4 md:mr-2', refreshing && 'animate-spin')} />
                  <span className="hidden md:inline">Refresh</span>
                </Button>
              )}
              <Button size="sm" onClick={() => handleConnect()} disabled={connecting}>
                <CalendarPlus className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">
                  {accounts.length > 0 ? 'Add another account' : 'Connect a calendar'}
                </span>
                <span className="md:hidden">{accounts.length > 0 ? 'Add' : 'Connect'}</span>
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            TrackMind reads your calendars so meetings show up alongside your tasks and the
            scheduler stops booking work over them. This is separate from signing in on the
            Sync tab: that decides where <em>your</em> data is kept, this decides whose
            meetings you can see. Connecting a calendar never moves your data anywhere.
          </p>

          {/* The three facts that answer "is this safe to point at my work account?" */}
          <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1.5">
            <div className="flex gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
              <span>
                <strong className="text-foreground">Read-only.</strong> TrackMind can never
                create, move or delete anything in a connected calendar.
              </span>
            </div>
            <div className="flex gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
              <span>
                <strong className="text-foreground">Titles stay on this device.</strong> Events
                are cached locally and never included in an export. So your calendars work on
                every device, the busy <em>times</em> are copied to your own cloud storage —
                the meeting names are not, unless you ask for them per account below.
              </span>
            </div>
            <div className="flex gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 text-green-600" />
              <span>
                <strong className="text-foreground">Separate from sign-in.</strong> Which
                calendars you read has nothing to do with where your data is stored — connecting
                a calendar doesn't move your data anywhere.
              </span>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
              <span className="text-destructive">{error}</span>
            </div>
          )}

          {lastRefreshedAt && (
            <p className="text-xs text-muted-foreground">
              Last checked {formatDateTime(lastRefreshedAt)} · checks again every few minutes and
              whenever you come back to the app.
            </p>
          )}

          {/* Signing in with Google stores your data in Drive; it deliberately
              does not read your calendar (different consent, different scope).
              Rather than leave people to discover that they must "sign in"
              twice, offer that account's calendar right here — Google
              pre-selects the account, so it's one approval, not a login. */}
          {storageEmail && !storageAccountConnected && !loading && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/40 p-3">
              <div className="min-w-0 text-sm">
                <p className="font-medium">Use {storageEmail}'s calendar too?</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You're signed in to this account for storage. Reading its calendar is a
                  separate permission, so Google will ask once — it won't sign you in again.
                </p>
              </div>
              <Button size="sm" onClick={() => handleConnect(storageEmail)} disabled={connecting}>
                <CalendarPlus className="h-4 w-4 mr-2" />
                Add its calendar
              </Button>
            </div>
          )}

          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}

          {!loading && accounts.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No calendars connected yet. You can connect more than one account — a personal one
              and a work one, for example.
            </p>
          )}
        </CardContent>
      </Card>

      <ShareCatchUp accounts={accounts} onEnable={setAccountSharing} />

      {accounts.map((account) => (
        <AccountCard
          key={account.id}
          account={account}
          // Stable, predictable order: your own calendar first, then
          // alphabetical. The cache returns them in storage-key order, which
          // is effectively random and reshuffles as calendars come and go.
          sources={sources
            .filter((s) => s.account_id === account.id)
            .sort((a, b) => {
              if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1
              return a.name.localeCompare(b.name)
            })}
          onDisconnect={() => disconnectAccount(account.id)}
          onReconnect={() => handleConnect(account.email)}
          onUpdateSource={updateSource}
          onSetSharing={(share, privacy) => setAccountSharing(account.id, share, privacy)}
        />
      ))}

      <OverlapPreference accounts={accounts} sources={sources} onUpdateSource={updateSource} />
    </div>
  )
}

const CATCH_UP_DISMISSED = 'trackmind.calendarShareHintDismissed'

/**
 * Offer sharing to accounts connected before it became the default.
 *
 * New accounts share times across devices from the moment they're connected.
 * Existing ones can't simply be flipped on — that would start uploading
 * somebody's calendar to their cloud storage without them asking, which is
 * precisely the thing the rest of this file is careful not to do. So they get
 * an offer instead, one tap, in the place they'd look anyway.
 */
function ShareCatchUp({
  accounts,
  onEnable,
}: {
  accounts: CalendarAccount[]
  onEnable: ReturnType<typeof useCalendar>['setAccountSharing']
}) {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(CATCH_UP_DISMISSED) === '1',
  )
  const [working, setWorking] = useState(false)

  const stranded = accounts.filter((a) => !a.mirrored && !a.share_across_devices)
  if (dismissed || stranded.length === 0) return null

  const enableAll = async () => {
    setWorking(true)
    try {
      // Times only. Titles are a separate, per-account decision, and this
      // banner is not the place to make it on someone's behalf.
      for (const account of stranded) await onEnable(account.id, true, 'busy')
    } finally {
      setWorking(false)
    }
  }

  return (
    <Card className="border-primary/40">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-2">
          <CloudUpload className="h-4 w-4 shrink-0 text-primary mt-0.5" />
          <div className="min-w-0 text-sm">
            <p className="font-medium">
              {stranded.length === 1
                ? 'This calendar only works on this device'
                : `${stranded.length} calendars only work on this device`}
            </p>
            <p className="text-muted-foreground mt-0.5">
              Copy the busy times into your own cloud storage and your other devices show them
              without signing in to {stranded.length === 1 ? 'that account' : 'those accounts'}.
              Meeting titles stay here.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={enableAll} disabled={working}>
            {working ? 'Turning on…' : 'Show on my other devices'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              localStorage.setItem(CATCH_UP_DISMISSED, '1')
              setDismissed(true)
            }}
          >
            Not now
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Which calendar leads when two of them are booked at the same time.
 *
 * Deliberately NOT a "which one wins" switch that hides the other. Both
 * meetings keep showing and both keep blocking time — the app has no way to
 * know that the 10am you ranked lower isn't the one that actually matters
 * today, and a meeting you never see is a meeting you miss. All this decides
 * is order and emphasis inside an overlap.
 *
 * Reordering is up/down arrows rather than drag: it's the same choice the list
 * rows made (dragging fights with scrolling on a phone), and this list is
 * short enough that two taps is nothing.
 */
function OverlapPreference({
  accounts,
  sources,
  onUpdateSource,
}: {
  accounts: CalendarAccount[]
  sources: CalendarSource[]
  onUpdateSource: ReturnType<typeof useCalendar>['updateSource']
}) {
  const [saving, setSaving] = useState(false)
  const accountEmail = new Map(accounts.map((a) => [a.id, a.email]))

  const ordered = sources
    .filter((s) => s.enabled)
    .sort(
      (a, b) =>
        calendarPriority(a) - calendarPriority(b) ||
        (a.is_primary === b.is_primary ? 0 : a.is_primary ? -1 : 1) ||
        a.name.localeCompare(b.name),
    )

  // Nothing to prefer between until there are two calendars to clash.
  if (ordered.length < 2) return null

  const move = async (index: number, delta: number) => {
    const next = [...ordered]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]

    setSaving(true)
    try {
      // Rewrite every rank, not just the two that moved: ranks default to the
      // same value, so a partial write would leave ties and the order would
      // drift back the next time something re-sorted.
      await Promise.all(next.map((source, i) => onUpdateSource(source.id, { priority: i + 1 })))
    } catch (e) {
      console.error('Failed to reorder calendars:', e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm md:text-base flex items-center gap-2">
          <Layers className="h-4 w-4" />
          When two calendars clash
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Overlapping meetings are shown together, never hidden — you stay busy for both. This
          is only about which one leads: the calendar at the top is shown first, and the others
          sit under it.
        </p>
        <div className="space-y-1.5">
          {ordered.map((source, index) => (
            <div
              key={source.id}
              className="flex items-center gap-2 rounded-lg border p-2"
            >
              <span
                className="h-3 w-3 shrink-0 rounded-full"
                style={{ backgroundColor: source.color ?? 'hsl(var(--muted-foreground))' }}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm">{source.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {accountEmail.get(source.account_id) ?? ''}
                </div>
              </div>
              {index === 0 && (
                <Badge variant="secondary" className="shrink-0 text-[10px]">
                  Leads
                </Badge>
              )}
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={index === 0 || saving}
                  onClick={() => move(index, -1)}
                  title={`Move ${source.name} up`}
                  aria-label={`Move ${source.name} up`}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={index === ordered.length - 1 || saving}
                  onClick={() => move(index, 1)}
                  title={`Move ${source.name} down`}
                  aria-label={`Move ${source.name} down`}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function AccountCard({
  account,
  sources,
  onDisconnect,
  onReconnect,
  onUpdateSource,
  onSetSharing,
}: {
  account: CalendarAccount
  sources: CalendarSource[]
  onDisconnect: () => void
  onReconnect: () => void
  onUpdateSource: ReturnType<typeof useCalendar>['updateSource']
  onSetSharing: (share: boolean, privacy?: CalendarPrivacy) => void
}) {
  const enabledCount = sources.filter((s) => s.enabled).length
  const isMirror = Boolean(account.mirrored)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm md:text-base flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate">{account.email}</span>
          <div className="flex items-center gap-2 shrink-0">
            {isMirror && (
              <Badge variant="outline" className="text-xs gap-1">
                <Laptop className="h-3 w-3" />
                <span className="hidden md:inline">From another device</span>
                <span className="md:hidden">Mirror</span>
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              {enabledCount}/{sources.length} on
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onDisconnect}
              title={isMirror ? 'Remove from this device' : 'Disconnect this account'}
              aria-label={isMirror ? 'Remove from this device' : 'Disconnect this account'}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {account.needs_reauth && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm mb-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="flex-1 min-w-0">This account needs to be reconnected.</span>
            <Button size="sm" variant="outline" onClick={onReconnect}>
              Reconnect
            </Button>
          </div>
        )}

        {isMirror ? (
          <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground mb-2 space-y-1">
            <p>
              Copied from another device where you're signed in to this account — you don't need
              to sign in to it here. It refreshes whenever that device does.
            </p>
            {account.mirrored_at && <p>Last updated {formatDateTime(account.mirrored_at)}.</p>}
            <p>
              To stop it appearing on your devices entirely, turn off "Available on my other
              devices" on the device that owns it.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-muted/30 p-3 mb-2 space-y-3">
            <div className="flex items-start gap-3">
              <Switch
                id={`${account.id}-share`}
                checked={Boolean(account.share_across_devices)}
                onCheckedChange={(v) => onSetSharing(v)}
                className="mt-0.5 shrink-0"
                aria-label={`Make ${account.email} available on my other devices`}
              />
              <div className="min-w-0">
                <Label
                  htmlFor={`${account.id}-share`}
                  className="text-sm flex items-center gap-1.5 cursor-pointer"
                >
                  <CloudUpload className="h-3.5 w-3.5" />
                  Available on my other devices
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Otherwise you'd have to sign in to this account on every device you use —
                  and for a work account on a personal laptop, you may not be able to. This
                  copies the enabled calendars into your own cloud storage so your other
                  devices read them without signing in.
                </p>
              </div>
            </div>

            {account.share_across_devices && (
              <div className="flex items-start gap-3 border-t pt-3">
                <Switch
                  id={`${account.id}-share-titles`}
                  checked={(account.share_privacy ?? 'busy') === 'full'}
                  onCheckedChange={(v) => onSetSharing(true, v ? 'full' : 'busy')}
                  className="mt-0.5 shrink-0"
                  aria-label={`Include event titles when sharing ${account.email}`}
                />
                <div className="min-w-0">
                  <Label
                    htmlFor={`${account.id}-share-titles`}
                    className="text-sm flex items-center gap-1.5 cursor-pointer"
                  >
                    {(account.share_privacy ?? 'busy') === 'full' ? (
                      <Eye className="h-3.5 w-3.5" />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" />
                    )}
                    Include event titles
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(account.share_privacy ?? 'busy') === 'full'
                      ? 'Your other devices see the meeting names. They are written to your own cloud storage.'
                      : 'Off: only the busy times and the calendar name travel. Titles stay on this device — including in your cloud storage. Recommended for a work calendar.'}
                  </p>
                  {/* One value per account, not per device — otherwise two
                      devices holding the same account would disagree and a
                      third would watch titles flicker. */}
                  <p className="text-[11px] text-muted-foreground/80 mt-1">
                    Applies to this account on all your devices.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {sources.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">No calendars found in this account.</p>
        )}

        {sources.map((source) => (
          <SourceRow
            key={source.id}
            source={source}
            onUpdate={onUpdateSource}
            isMirror={isMirror}
          />
        ))}
      </CardContent>
    </Card>
  )
}

function SourceRow({
  source,
  onUpdate,
  isMirror = false,
}: {
  source: CalendarSource
  onUpdate: ReturnType<typeof useCalendar>['updateSource']
  /** Mirrored calendars are fetched elsewhere, so only display choices apply here. */
  isMirror?: boolean
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-lg border">
      <div className="flex items-center gap-2 px-2 py-2 min-h-[44px]">
        <button
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          onClick={() => setOpen(!open)}
          disabled={!source.enabled}
          aria-label={`Options for ${source.name}`}
        >
          {source.enabled ? (
            open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <span
            className="h-3 w-3 rounded-full shrink-0 border"
            style={{ backgroundColor: source.color ?? 'transparent' }}
          />
          <span className="truncate text-sm">{source.name}</span>
          {source.is_primary && (
            <Badge variant="outline" className="text-[10px] shrink-0 hidden md:inline-flex">
              Primary
            </Badge>
          )}
          {source.enabled && source.privacy === 'busy' && (
            <EyeOff className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Busy only" />
          )}
        </button>
        <Switch
          checked={source.enabled}
          onCheckedChange={(checked) => onUpdate(source.id, { enabled: checked })}
          aria-label={`Show ${source.name}`}
        />
      </div>

      {source.enabled && open && (
        <div className="border-t px-3 py-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <ToggleOption
            id={`${source.id}-blocks`}
            label="Block time for the scheduler"
            hint="Meetings here stop tasks being scheduled over them."
            checked={source.blocks_time}
            onChange={(v) => onUpdate(source.id, { blocks_time: v })}
          />
          {/* What gets fetched — and therefore what's filtered out or
              stripped — is decided by the device that owns the account, so
              those choices aren't offered on a mirror. */}
          {isMirror ? (
            <p className="text-xs text-muted-foreground md:col-span-2">
              Titles, all-day events and declined invites follow the settings on the device that
              owns this account.
            </p>
          ) : (
            <>
              <ToggleOption
                id={`${source.id}-privacy`}
                label="Hide event titles"
                hint="Keep only the times. Titles are dropped before anything is saved — useful for a work calendar."
                checked={source.privacy === 'busy'}
                onChange={(v) => onUpdate(source.id, { privacy: v ? 'busy' : 'full' })}
                icon={source.privacy === 'busy' ? EyeOff : Eye}
              />
              <ToggleOption
                id={`${source.id}-allday`}
                label="Include all-day events"
                hint="Birthdays and holidays. They never block time."
                checked={source.include_all_day}
                onChange={(v) => onUpdate(source.id, { include_all_day: v })}
              />
              <ToggleOption
                id={`${source.id}-declined`}
                label="Include declined invites"
                checked={source.include_declined}
                onChange={(v) => onUpdate(source.id, { include_declined: v })}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function ToggleOption({
  id,
  label,
  hint,
  checked,
  onChange,
  icon: Icon,
}: {
  id: string
  label: string
  hint?: string
  checked: boolean
  onChange: (value: boolean) => void
  icon?: React.ComponentType<{ className?: string }>
}) {
  return (
    <div className="flex items-start gap-3">
      <Switch id={id} checked={checked} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
      <div className="min-w-0">
        <Label htmlFor={id} className="text-sm flex items-center gap-1.5 cursor-pointer">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {label}
        </Label>
        {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
      </div>
    </div>
  )
}
